import { useEffect, useState } from "react";
import { api, apiBase } from "../lib/api";
import { Banner, Button, Field } from "./Field";

type Asset = { id: string; filename: string; mime: string; alt: string | null; width: number | null; height: number | null };

export function AssetPicker({ siteSlug, open, onPick, onClose }: { siteSlug: string; open: boolean; onPick: (a: Asset) => void; onClose: () => void }) {
  const [items, setItems] = useState<Asset[]>([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    api.get<{ items: Asset[] }>(`/api/sites/${siteSlug}/assets${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setItems(r.items))
      .catch((e) => setErr((e as Error).message));
  }, [open, siteSlug, q]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-[rgba(12,13,14,0.55)] backdrop-blur-[2px] z-[1000] grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--color-paper)] border border-[var(--color-rule)] rounded-[var(--radius-md)] shadow-[0_24px_60px_-12px_rgba(12,13,14,0.35)] w-full max-w-[760px] max-h-[88vh] flex flex-col reveal reveal-1"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--color-rule)]">
          <div>
            <div className="section-no mb-1">№ Picker · Asset</div>
            <h2 className="display text-2xl text-[var(--color-ink)]">Pick an <em>asset</em></h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </header>
        <div className="px-6 pt-5">
          <Field label="Search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="filename or alt text" autoFocus />
          {err && <Banner kind="error">{err}</Banner>}
        </div>
        <div className="px-6 pb-6 overflow-auto flex-1">
          {items.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-[var(--color-muted)]">No assets match.</div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
              {items.map((a) => (
                <button
                  key={a.id}
                  onClick={() => { onPick(a); onClose(); }}
                  className="bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] p-2 text-left cursor-pointer hover:border-[var(--color-ink)] transition-colors"
                >
                  {a.mime.startsWith("image/") ? (
                    <img
                      src={`${apiBase}/api/sites/${siteSlug}/assets/${a.id}/raw`}
                      alt={a.alt ?? ""}
                      className="w-full aspect-square object-cover rounded-[3px] bg-[var(--color-paper-2)]"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-[var(--color-paper-2)] grid place-items-center rounded-[3px] text-[11px] font-mono uppercase tracking-[0.08em] text-[var(--color-muted)]">
                      {a.mime.split("/")[1] ?? "file"}
                    </div>
                  )}
                  <div className="text-[11.5px] mt-2 break-all text-[var(--color-ink)] leading-tight">{a.filename}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
